/**
 * AudioPodcastItem - Display component for audio podcasts
 */
import { Headphones, RefreshCw, X, Check, Trash2, Calendar } from 'lucide-react';

export const AudioPodcastItem = ({ 
  podcast, 
  refreshing, 
  onRefresh, 
  onToggleActive, 
  onDelete 
}) => {
  return (
    <div
      className={`bg-zinc-900 rounded-xl p-4 border ${
        podcast.is_active ? 'border-white/10' : 'border-red-500/30 opacity-60'
      }`}
      data-testid={`podcast-${podcast.id}`}
    >
      <div className="flex items-start gap-4">
        {/* Podcast Image */}
        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-zinc-800">
          {podcast.image_url ? (
            <img src={podcast.image_url} alt={podcast.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Headphones className="w-8 h-8 text-zinc-600" />
            </div>
          )}
        </div>

        {/* Podcast Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div>
              <h4 className="text-white font-semibold truncate">{podcast.name}</h4>
              <p className="text-zinc-400 text-sm truncate">{podcast.author || 'Unknown author'}</p>
            </div>
            {!podcast.is_active && (
              <span className="bg-red-500/20 text-red-500 text-xs px-2 py-1 rounded">Inactive</span>
            )}
          </div>

          <div className="flex items-center gap-4 mt-2 text-sm text-zinc-500">
            <span className="flex items-center gap-1">
              <Headphones className="w-4 h-4" />
              {podcast.episode_count || 0} episodes
            </span>
            <span className="bg-zinc-800 px-2 py-0.5 rounded text-xs">{podcast.category}</span>
            {podcast.last_updated && (
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Updated {new Date(podcast.last_updated).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => onRefresh(podcast.id)}
            disabled={refreshing}
            className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
            title="Refresh episodes from RSS"
          >
            <RefreshCw className={`w-4 h-4 text-zinc-400 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
          <button
            onClick={() => onToggleActive(podcast.id, podcast.is_active)}
            className={`p-2 rounded-lg transition-colors ${
              podcast.is_active ? 'hover:bg-red-500/20' : 'hover:bg-green-500/20'
            }`}
            title={podcast.is_active ? 'Deactivate' : 'Activate'}
          >
            {podcast.is_active ? (
              <X className="w-4 h-4 text-red-500" />
            ) : (
              <Check className="w-4 h-4 text-green-500" />
            )}
          </button>
          <button
            onClick={() => onDelete(podcast.id)}
            className="p-2 hover:bg-red-500/20 rounded-lg transition-colors"
            title="Delete podcast"
          >
            <Trash2 className="w-4 h-4 text-red-500" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AudioPodcastItem;
